import { GripVertical, Plus, Trash2 } from 'lucide-react';

import { Button, Input, Label, Textarea } from '@workspace/shadcn';

/**
 * Authored lists (objectives, target areas, languages) are stored as ordered
 * `String[]`, so the editor works on the whole array and the caller submits it
 * whole. Blank rows are kept while typing and dropped on submit by the caller.
 */
export function TextListField({
  label,
  value,
  onChange,
  placeholder,
  addLabel = 'Add item',
  multiline = false,
  reorderable = true,
}: {
  label: string;
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  addLabel?: string;
  multiline?: boolean;
  /**
   * Whether the stored order is meaningful to the reader. Objectives and target
   * areas are presented as ordered lists, so they need the control; a set like
   * languages does not, and the handle is only clutter there.
   */
  reorderable?: boolean;
}) {
  const replaceAt = (index: number, next: string) =>
    onChange(value.map((item, i) => (i === index ? next : item)));

  const move = (index: number, delta: number) => {
    const target = index + delta;
    if (target < 0 || target >= value.length) return;
    const next = [...value];
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  };

  return (
    <div className="space-y-2">
      <Label>{label}</Label>

      {value.length === 0 && (
        <p className="text-muted-foreground text-sm">Nothing added yet.</p>
      )}

      <div className="space-y-2">
        {value.map((item, index) => (
          <div key={index} className="flex items-start gap-2">
            {reorderable && (
              <div className="flex flex-col pt-1">
                <button
                  type="button"
                  aria-label="Move up"
                  disabled={index === 0}
                  onClick={() => move(index, -1)}
                  className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                >
                  <GripVertical className="size-4" />
                </button>
              </div>
            )}
            {multiline ? (
              <Textarea
                rows={2}
                value={item}
                placeholder={placeholder}
                onChange={(e) => replaceAt(index, e.target.value)}
              />
            ) : (
              <Input
                type="text"
                value={item}
                placeholder={placeholder}
                onChange={(e) => replaceAt(index, e.target.value)}
              />
            )}
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label="Remove"
              className="text-destructive shrink-0"
              onClick={() => onChange(value.filter((_, i) => i !== index))}
            >
              <Trash2 className="size-4" />
            </Button>
          </div>
        ))}
      </div>

      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => onChange([...value, ''])}
      >
        <Plus className="me-1 size-3.5" />
        {addLabel}
      </Button>
    </div>
  );
}
